/**
 * The response chart's cache of series tiles, and the reads that fill it.
 *
 * The chart never asks for "the window on screen". It asks for the aligned
 * tiles (`tileStarts`) that cover the window plus a margin, so a small scroll
 * costs one new tile, a tile already read is a hit however the reader got
 * back to it, and the next stretch is usually in hand before it scrolls in.
 *
 * The rules this file keeps, each one a way the chart used to be or could be
 * quietly wrong:
 *
 *  - At most MAX_IN_FLIGHT reads at once, the tiles nearest the middle of the
 *    wanted window first, so the visible part arrives before the margins. A
 *    tile that scrolled out of the want before its turn is never asked for.
 *  - A tile stays `open` until the server calls it final (`tileOpen`): the
 *    newest check when it was read (`liveKey`, the server's clock) is a check
 *    interval and a check's writing time past its end, and the read did not
 *    end on an outage that has no end yet (`openEnd`). Never the browser's
 *    clock: one a few minutes fast, or a read between a tile's end and the
 *    next check, would freeze an outage cut short at the tile's edge. When a
 *    new check lands (`liveKey` changes) open tiles are read again in the
 *    background and keep drawing their old points meanwhile:
 *    stale-while-revalidate, so the line never blinks out once a minute.
 *  - A refresh of a bucketed tile reads only its tail (`refreshFrom`) and
 *    splices it in (`spliceTile`). The overview's tile holding now can be
 *    sixty days long, and read whole at every new check it would pull tens of
 *    thousands of rows a minute for one open page.
 *  - A new check that lands while a tile's read is out marks that same entry
 *    stale, in place, and the read carries the mark over: one more refresh
 *    follows instead of the check being lost, and the read's slot is always
 *    given back. (Replacing the entry instead made the read's answer look
 *    foreign, so it was dropped and its slot never freed.)
 *  - A failed first read leaves an error on that tile and the window says so
 *    (`failed`) with a retry, instead of a chart that silently stops early.
 *    A failed refresh keeps the old points; the next check tries again.
 *  - Keys carry the monitor id and every read is aborted when the monitor
 *    changes or the chart unmounts, so one site's tiles can never be drawn
 *    under another's name.
 *
 * The Map lives in a ref (reads resolve outside React) and `version` is the
 * state that makes a finished read re-render; `cover` changes identity with
 * it, so a memo over `cover` is recomputed exactly when a tile arrived.
 */
import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  getMonitorSeries,
  refreshFrom,
  spliceTile,
  tileKey,
  tileMs,
  tileOpen,
  tileStarts,
  type SeriesTile,
} from "../../../../lib/monitoring";

/** Reads out at once; more would only queue in the browser behind each other. */
const MAX_IN_FLIGHT = 3;
/** Tiles kept before the least recently wanted ones are dropped (45 days of raw 6 h tiles is 180). */
const MAX_TILES = 240;

interface Entry {
  step: number;
  start: number;
  /** The last good read; kept while a refresh is out or after a refresh failed. */
  tile: SeriesTile | null;
  /** "error" only for a tile that never had a good read. */
  status: "loading" | "ready" | "error";
  /** A newer check may have landed in it since the read. Set in place, so a read already out keeps it. */
  stale: boolean;
  /** Not final yet on the server's word (`tileOpen`): later checks can still change it. */
  open: boolean;
  /** The site's newest check (epoch ms) when the last good read started; NaN before one. Where the next refresh reads from. */
  seenAt: number;
  /** Set while a read is out. */
  ctrl: AbortController | null;
  /** When it was last wanted, for eviction. */
  used: number;
}

/** A window to hold, margin included, clipped to the history. */
export interface TileWant {
  step: number;
  from: number;
  to: number;
}

export interface TileCover {
  /** Every tile in hand that meets the padded window, in no particular order. */
  tiles: SeriesTile[];
  /** Every tile that meets [from, to) itself is in hand. */
  complete: boolean;
  /** Some tile of [from, to) is still being read. */
  loading: boolean;
  /** Some tile of [from, to) failed and waits for `retry`. */
  failed: boolean;
}

const keyOf = (monitorId: string, step: number, start: number) => `${monitorId}|${tileKey(step, start)}`;

/** The server's newest check as epoch ms; NaN when there is none or it does not parse. */
const liveTime = (liveKey: string | null) => (liveKey ? Date.parse(liveKey) : Number.NaN);

export function useSeriesTiles(monitorId: string, want: TileWant | null, liveKey: string | null, checkMinutes: number) {
  const cache = useRef(new Map<string, Entry>());
  const [version, bump] = useReducer((n: number) => n + 1, 0);
  const wantRef = useRef<TileWant | null>(want);
  const tick = useRef(0);
  // The newest check the page has seen: each read notes it as the server's "now".
  const seenLive = useRef(liveKey);

  // A different site or an unmount: nothing of the old one may land. The same
  // cleanup is what React's development double mount runs, and the fetch
  // effect below then asks again from an empty cache.
  useEffect(() => {
    const map = cache.current;
    return () => {
      for (const entry of map.values()) entry.ctrl?.abort();
      map.clear();
    };
  }, [monitorId]);

  const pump = useCallback(() => {
    const map = cache.current;
    const w = wantRef.current;
    if (!w) return;
    tick.current += 1;
    const len = tileMs(w.step);
    const mid = (w.from + w.to) / 2;
    const wanted = tileStarts(w.step, w.from, w.to).map((start) => ({ start, key: keyOf(monitorId, w.step, start) }));
    for (const { key } of wanted) {
      const entry = map.get(key);
      if (entry) entry.used = tick.current;
    }

    let inFlight = 0;
    for (const entry of map.values()) if (entry.ctrl) inFlight += 1;
    const todo = wanted
      .filter(({ key }) => {
        const entry = map.get(key);
        return !entry || (entry.stale && !entry.ctrl && entry.status === "ready");
      })
      .sort((a, b) => Math.abs(a.start + len / 2 - mid) - Math.abs(b.start + len / 2 - mid));

    for (const { start, key } of todo) {
      if (inFlight >= MAX_IN_FLIGHT) break;
      inFlight += 1;
      const prev = map.get(key);
      const ctrl = new AbortController();
      const step = w.step;
      const end = start + len;
      // A refresh reads only what can have changed since the last good read; a first read, all of it.
      const from = prev?.tile ? refreshFrom(prev.tile, prev.seenAt) : start;
      const seenAt = liveTime(seenLive.current);
      const entry: Entry = {
        step,
        start,
        tile: prev?.tile ?? null,
        status: prev?.tile ? "ready" : "loading",
        // This read answers every mark made so far; a check that lands while it is out marks it again.
        stale: false,
        open: prev?.open ?? true,
        seenAt: prev?.seenAt ?? Number.NaN,
        ctrl,
        used: tick.current,
      };
      map.set(key, entry);
      getMonitorSeries(monitorId, { from, to: end, step }, ctrl.signal).then(
        (res) => {
          if (ctrl.signal.aborted || map.get(key) !== entry) return;
          const base: SeriesTile = entry.tile ?? { step, start, end, points: [], downSpans: [] };
          map.set(key, {
            ...entry,
            tile: spliceTile(base, from, res),
            status: "ready",
            open: tileOpen(end, seenAt, checkMinutes, res.openEnd === true),
            seenAt,
            ctrl: null,
          });
          bump();
        },
        () => {
          if (ctrl.signal.aborted || map.get(key) !== entry) return;
          // A failed refresh keeps the last good tile, and the mark and `seenAt` that go with it.
          map.set(key, entry.tile ? { ...entry, status: "ready", ctrl: null } : { ...entry, status: "error", ctrl: null });
          bump();
        },
      );
    }

    // Drop the tiles wanted longest ago; never one being read.
    if (map.size > MAX_TILES) {
      const idle = [...map.entries()].filter(([, e]) => !e.ctrl).sort((a, b) => a[1].used - b[1].used);
      for (const [key] of idle.slice(0, map.size - MAX_TILES)) map.delete(key);
    }
  }, [monitorId, checkMinutes]);

  // A new check: open tiles are read again (their points stay drawn), and a
  // tile that failed gets another go without a click. The mark is set on the
  // entry itself, so a read already out keeps its identity and carries the
  // mark over. Declared before the fetch effect: on the render a new check
  // arrives, the reads that effect starts already note it.
  useEffect(() => {
    if (seenLive.current === liveKey) return;
    seenLive.current = liveKey;
    const map = cache.current;
    for (const [key, entry] of map) {
      if (entry.status === "error") map.delete(key);
      else if (entry.open) entry.stale = true;
    }
    bump();
  }, [liveKey]);

  // Each finished read bumps `version`, which runs this again and starts the
  // next queued tile; a scroll changes the want and does the same.
  const wantStep = want?.step;
  const wantFrom = want?.from;
  const wantTo = want?.to;
  useEffect(() => {
    wantRef.current = wantStep === undefined ? null : { step: wantStep, from: wantFrom!, to: wantTo! };
    pump();
  }, [wantStep, wantFrom, wantTo, version, pump]);

  /** What the cache holds for [from, to) at `step`, with `pad` more on each side for the line's neighbours. */
  const cover = useCallback(
    (step: number, from: number, to: number, pad = 0): TileCover => {
      const map = cache.current;
      const len = tileMs(step);
      const tiles: SeriesTile[] = [];
      let complete = true;
      let loading = false;
      let failed = false;
      for (const start of tileStarts(step, from - pad, to + pad)) {
        const entry = map.get(keyOf(monitorId, step, start));
        if (entry?.tile) {
          tiles.push(entry.tile);
          continue;
        }
        if (start >= to || start + len <= from) continue;
        complete = false;
        if (entry?.status === "error") failed = true;
        else loading = true;
      }
      return { tiles, complete, loading, failed };
    },
    // `version` is not read here: it is what tells a memo over `cover` that the Map changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monitorId, version],
  );

  /** Ask again for every tile whose first read failed. */
  const retry = useCallback(() => {
    const map = cache.current;
    for (const [key, entry] of map) if (entry.status === "error") map.delete(key);
    bump();
  }, []);

  return { cover, retry };
}
